/* ============================================================
   BYOB (Build Your Own Bundle)
   Clean rebuild — Apr 2026

   Architecture
   - Single class instantiated once per [data-byob] root
   - All clicks handled via ONE document-level capture-phase listener
     (this guarantees no other theme script can intercept events
     during the bubble phase, which was the root cause of past bugs)
   - State lives in a Map keyed by "productId:variantId"
   - Every state change calls render() — single source of truth
   ============================================================ */

(function () {
  'use strict';

  // ----- Money helpers -------------------------------------------------------

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = whole + '.' + parts[1];
    if (!format) return '$' + formatted;
    return format.replace(/\{\{\s*amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?\s*\}\}/g, formatted);
  }

  // ----- Main class ----------------------------------------------------------

  class Byob {
    constructor(root) {
      this.root = root;
      this.config = this._readConfig();
      this.featured = this._readFeatured();
      this.items = new Map(); // key = "productId:variantId" -> { productId, variantId, title, image, price, url, qty }
      this.activeTab = null;

      this._cacheNodes();
      this._bindEvents();
      this._initFirstTab();
      this.render();
    }

    // ----- Init helpers ------------------------------------------------------

    _readConfig() {
      const node = this.root.querySelector('[data-byob-config]');
      if (!node) return { tiers: [], minItems: 0, ctaText: 'Add bundle to cart', emptyText: '', moneyFormat: '${{amount}}' };
      try { return JSON.parse(node.textContent); }
      catch (e) { console.error('[BYOB] bad config JSON', e); return {}; }
    }

    _readFeatured() {
      const node = this.root.querySelector('[data-byob-featured]');
      if (!node) return [];
      try { return JSON.parse(node.textContent); }
      catch (e) { console.error('[BYOB] bad featured JSON', e); return []; }
    }

    _cacheNodes() {
      const r = this.root;
      this.nodes = {
        // Mobile sticky tray (compact)
        tray: r.querySelector('[data-byob-tray]'),
        trayCount: r.querySelector('[data-byob-tray-count]'),
        traySavings: r.querySelector('[data-byob-tray-savings]'),
        trayTotal: r.querySelector('[data-byob-tray-total]'),
        barFill: r.querySelector('[data-byob-bar-fill]'),
        // Desktop sidebar (persistent bundle panel)
        sidebar: r.querySelector('[data-byob-sidebar]'),
        sidebarCount: r.querySelector('[data-byob-sidebar-count]'),
        sidebarItems: r.querySelector('[data-byob-sidebar-items]'),
        sidebarSubtotal: r.querySelector('[data-byob-sidebar-subtotal]'),
        sidebarSavingsRow: r.querySelector('[data-byob-sidebar-savings-row]'),
        sidebarSavings: r.querySelector('[data-byob-sidebar-savings]'),
        sidebarTotal: r.querySelector('[data-byob-sidebar-total]'),
        sidebarHint: r.querySelector('[data-byob-next-tier-hint]'),
        sidebarTierChips: r.querySelectorAll('.byob-sidebar [data-byob-tier-chip]'),
        sidebarProgress: r.querySelector('[data-byob-sidebar-progress]'),
        sidebarNext: r.querySelector('[data-byob-sidebar-next]'),
        sidebarNextText: r.querySelector('[data-byob-sidebar-next-text]'),
        ctaText: r.querySelector('[data-byob-cta-text]'),
        error: r.querySelector('[data-byob-error]'),
        // Mobile drawer (mirrors sidebar content)
        drawer: r.querySelector('[data-byob-drawer]'),
        drawerCount: r.querySelector('[data-byob-drawer-count]'),
        drawerItems: r.querySelector('[data-byob-drawer-items]'),
        drawerSubtotal: r.querySelector('[data-byob-drawer-subtotal]'),
        drawerDiscRow: r.querySelector('[data-byob-drawer-discount-row]'),
        drawerDiscount: r.querySelector('[data-byob-drawer-discount]'),
        drawerTotal: r.querySelector('[data-byob-drawer-total]'),
        drawerHint: r.querySelector('[data-byob-drawer-hint]'),
        drawerTierChips: r.querySelectorAll('.byob-drawer [data-byob-tier-chip]'),
        // Featured bundles modal
        modal: r.querySelector('[data-byob-modal]'),
      };
    }

    _initFirstTab() {
      const firstTab = this.root.querySelector('[data-byob-tab].is-active');
      if (firstTab) this.activeTab = firstTab.getAttribute('data-byob-tab');
    }

    // ----- Event delegation (CAPTURE PHASE) ----------------------------------
    // All clicks for this builder go through one handler at the document level.
    // Capture phase ensures NO other theme script can intercept first.

    _bindEvents() {
      this._onClick = (e) => {
        if (!this.root.contains(e.target)) return;
        this._handleClick(e);
      };
      this._onChange = (e) => {
        if (!this.root.contains(e.target)) return;
        this._handleChange(e);
      };
      this._onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        if (this.nodes.modal && !this.nodes.modal.hidden) this._closeModal();
        if (this.nodes.drawer && !this.nodes.drawer.hidden) this._closeDrawer();
      };
      document.addEventListener('click', this._onClick, true);
      document.addEventListener('change', this._onChange, true);
      document.addEventListener('keydown', this._onKeydown);

      // Touch swipe on tile galleries (mobile)
      this._touchState = null;
      this._onTouchStart = (e) => {
        const gallery = e.target.closest && e.target.closest('[data-byob-gallery].has-gallery');
        if (!gallery) return;
        const t = e.touches[0];
        this._touchState = { gallery: gallery, x: t.clientX, y: t.clientY };
      };
      this._onTouchEnd = (e) => {
        const s = this._touchState;
        if (!s) return;
        this._touchState = null;
        const t = (e.changedTouches && e.changedTouches[0]) || null;
        if (!t) return;
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;
        // Only count as a swipe if horizontal motion dominates and exceeds threshold
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this._slideBy(s.gallery, dx < 0 ? 1 : -1);
        }
      };
      document.addEventListener('touchstart', this._onTouchStart, { passive: true });
      document.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }

    _handleClick(e) {
      const t = e.target;
      const closest = (sel) => t.closest && t.closest(sel);

      // Hero CTA → scroll to grid
      if (closest('[data-byob-scroll]')) {
        e.preventDefault();
        const grid = this.root.querySelector('#byob-grid');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      // Tab switch
      const tab = closest('[data-byob-tab]');
      if (tab) {
        e.preventDefault();
        this._switchTab(tab.getAttribute('data-byob-tab'));
        return;
      }

      // Add to bundle
      const addBtn = closest('[data-byob-add]');
      if (addBtn) {
        e.preventDefault();
        e.stopPropagation();
        const tile = addBtn.closest('[data-byob-tile]');
        if (tile) this._addItem(tile, 1);
        return;
      }

      // Increment / decrement on tile
      const incBtn = closest('[data-byob-increment]');
      if (incBtn) {
        e.preventDefault();
        e.stopPropagation();
        const tile = incBtn.closest('[data-byob-tile]');
        if (tile) this._addItem(tile, 1);
        return;
      }
      const decBtn = closest('[data-byob-decrement]');
      if (decBtn) {
        e.preventDefault();
        e.stopPropagation();
        const tile = decBtn.closest('[data-byob-tile]');
        if (tile) this._addItem(tile, -1);
        return;
      }

      // Gallery navigation (prev / next / dot)
      const prevBtn = closest('[data-byob-prev]');
      if (prevBtn) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = prevBtn.closest('[data-byob-gallery]');
        if (gallery) this._slideBy(gallery, -1);
        return;
      }
      const nextBtn = closest('[data-byob-next]');
      if (nextBtn) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = nextBtn.closest('[data-byob-gallery]');
        if (gallery) this._slideBy(gallery, 1);
        return;
      }
      const dotBtn = closest('[data-byob-dot]');
      if (dotBtn) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = dotBtn.closest('[data-byob-gallery]');
        const idx = parseInt(dotBtn.getAttribute('data-byob-dot'), 10);
        if (gallery && !isNaN(idx)) this._goToSlide(gallery, idx);
        return;
      }

      // Featured bundle add (works in inline strip AND modal)
      const bundleBtn = closest('[data-byob-bundle-add]');
      if (bundleBtn) {
        e.preventDefault();
        e.stopPropagation();
        this._addBundle(bundleBtn.getAttribute('data-bundle-id'));
        return;
      }

      // Modal open/close
      if (closest('[data-byob-modal-open]')) { e.preventDefault(); this._openModal(); return; }
      if (closest('[data-byob-modal-close]')) { e.preventDefault(); this._closeModal(); return; }

      // Drawer open/close (mobile only — the "View bundle" button + close X)
      if (closest('[data-byob-drawer-open]')) { e.preventDefault(); this._openDrawer(); return; }
      if (closest('[data-byob-drawer-close]')) { e.preventDefault(); this._closeDrawer(); return; }

      // Drawer item remove / increment / decrement
      const drawerInc = closest('[data-byob-drawer-inc]');
      if (drawerInc) { e.preventDefault(); this._changeDrawerQty(drawerInc.getAttribute('data-key'), 1); return; }
      const drawerDec = closest('[data-byob-drawer-dec]');
      if (drawerDec) { e.preventDefault(); this._changeDrawerQty(drawerDec.getAttribute('data-key'), -1); return; }
      const drawerRem = closest('[data-byob-drawer-remove]');
      if (drawerRem) { e.preventDefault(); this._removeItem(drawerRem.getAttribute('data-key')); return; }

      // Checkout / Add to cart
      const checkoutBtn = closest('[data-byob-checkout]');
      if (checkoutBtn) {
        e.preventDefault();
        if (checkoutBtn.disabled) return;
        const action = checkoutBtn.getAttribute('data-byob-action') || 'checkout';
        this._submit(checkoutBtn, action);
        return;
      }
    }

    _handleChange(e) {
      const variantSelect = e.target.closest && e.target.closest('[data-byob-variant]');
      if (variantSelect) {
        const tile = variantSelect.closest('[data-byob-tile]');
        if (!tile) return;
        const opt = variantSelect.options[variantSelect.selectedIndex];
        const newVariantId = opt.value;
        const newPrice = parseInt(opt.getAttribute('data-price'), 10);
        const newImage = opt.getAttribute('data-image');
        const imgIdxAttr = opt.getAttribute('data-image-index');

        // Update tile's data attributes
        tile.setAttribute('data-variant-id', newVariantId);
        tile.setAttribute('data-price', newPrice);
        if (newImage) tile.setAttribute('data-product-image', newImage);
        const priceEl = tile.querySelector('[data-byob-tile-price]');
        if (priceEl) priceEl.textContent = formatMoney(newPrice, this.config.moneyFormat);

        // Sync gallery to the variant's featured image (if mapped)
        if (imgIdxAttr !== null) {
          const idx = parseInt(imgIdxAttr, 10);
          const gallery = tile.querySelector('[data-byob-gallery]');
          if (gallery && !isNaN(idx)) this._goToSlide(gallery, idx);
        }

        // If this product was in bundle under old variant, leave that line alone.
        // Future adds will use the new variant.
        this._renderTileState(tile);
      }
    }

    // ----- Gallery slider ----------------------------------------------------

    _goToSlide(gallery, idx) {
      const slides = gallery.querySelector('[data-byob-slides]');
      if (!slides) return;
      const items = slides.querySelectorAll('[data-byob-slide]');
      const total = items.length;
      if (total === 0) return;
      const clamped = Math.max(0, Math.min(idx, total - 1));
      slides.style.transform = 'translateX(-' + (clamped * 100) + '%)';
      items.forEach((el, i) => el.classList.toggle('is-active', i === clamped));
      const dots = gallery.querySelectorAll('[data-byob-dot]');
      dots.forEach((el, i) => el.classList.toggle('is-active', i === clamped));
      gallery.setAttribute('data-current-slide', String(clamped));
    }

    _slideBy(gallery, delta) {
      const items = gallery.querySelectorAll('[data-byob-slide]');
      const total = items.length;
      if (total === 0) return;
      const current = parseInt(gallery.getAttribute('data-current-slide') || '0', 10);
      // Wrap around so prev from first → last and next from last → first
      const next = (current + delta + total) % total;
      this._goToSlide(gallery, next);
    }

    // ----- State mutations ---------------------------------------------------

    _addItem(tile, delta) {
      const productId = tile.getAttribute('data-product-id');
      const variantId = tile.getAttribute('data-variant-id');
      const key = productId + ':' + variantId;
      const existing = this.items.get(key);

      if (existing) {
        const newQty = existing.qty + delta;
        if (newQty <= 0) {
          this.items.delete(key);
        } else {
          existing.qty = newQty;
          this.items.set(key, existing);
        }
      } else if (delta > 0) {
        this.items.set(key, {
          key: key,
          productId: productId,
          variantId: variantId,
          title: tile.getAttribute('data-product-title') || '',
          image: tile.getAttribute('data-product-image') || '',
          url: tile.getAttribute('data-product-url') || '',
          price: parseInt(tile.getAttribute('data-price'), 10) || 0,
          category: tile.getAttribute('data-category') || '',
          qty: delta,
        });
      }

      this.render();
    }

    _addBundle(bundleId) {
      const bundle = this.featured.find((b) => b.id === bundleId);
      if (!bundle) return;
      bundle.products.forEach((p) => {
        const key = p.id + ':' + p.variantId;
        const existing = this.items.get(key);
        if (existing) {
          existing.qty += 1;
        } else {
          this.items.set(key, {
            key: key,
            productId: String(p.id),
            variantId: String(p.variantId),
            title: p.title,
            image: p.image,
            url: p.url,
            price: p.price,
            category: '',
            qty: 1,
          });
        }
      });
      if (this.nodes.modal && !this.nodes.modal.hidden) this._closeModal();
      this.render();
    }

    _changeDrawerQty(key, delta) {
      const item = this.items.get(key);
      if (!item) return;
      const newQty = item.qty + delta;
      if (newQty <= 0) this.items.delete(key);
      else { item.qty = newQty; this.items.set(key, item); }
      this.render();
    }

    _removeItem(key) {
      this.items.delete(key);
      this.render();
    }

    // ----- Tabs --------------------------------------------------------------

    _switchTab(handle) {
      if (this.activeTab === handle) return;
      this.activeTab = handle;
      this.root.querySelectorAll('[data-byob-tab]').forEach((tab) => {
        const isActive = tab.getAttribute('data-byob-tab') === handle;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-byob-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.getAttribute('data-byob-panel') === handle);
      });
    }

    // ----- Modal / drawer ----------------------------------------------------

    _openModal()  { if (this.nodes.modal)  { this.nodes.modal.hidden = false;  document.body.style.overflow = 'hidden'; } }
    _closeModal() { if (this.nodes.modal)  { this.nodes.modal.hidden = true;   document.body.style.overflow = ''; } }
    _openDrawer() { if (this.nodes.drawer) { this.nodes.drawer.hidden = false; document.body.style.overflow = 'hidden'; } }
    _closeDrawer(){ if (this.nodes.drawer) { this.nodes.drawer.hidden = true;  document.body.style.overflow = ''; } }

    // ----- Computed totals ---------------------------------------------------

    _totals() {
      let count = 0;
      let subtotal = 0;
      this.items.forEach((it) => { count += it.qty; subtotal += it.price * it.qty; });

      const tiers = this.config.tiers || [];
      let activeIdx = -1;
      for (let i = 0; i < tiers.length; i++) {
        if (subtotal >= tiers[i].threshold * 100) activeIdx = i;
      }
      const activePct = activeIdx >= 0 ? tiers[activeIdx].percent : 0;
      const discount = Math.round(subtotal * activePct / 100);
      const total = subtotal - discount;
      const nextTier = tiers[activeIdx + 1];

      return { count, subtotal, activeIdx, activePct, discount, total, nextTier };
    }

    // ----- Render ------------------------------------------------------------

    render() {
      const t = this._totals();
      this._renderTiles();
      this._renderSidebar(t);
      this._renderDrawer(t);
      this._renderTray(t);
      this._renderCtas(t);
    }

    _renderTiles() {
      this.root.querySelectorAll('[data-byob-tile]').forEach((tile) => this._renderTileState(tile));
    }

    _renderTileState(tile) {
      const productId = tile.getAttribute('data-product-id');
      const variantId = tile.getAttribute('data-variant-id');
      const item = this.items.get(productId + ':' + variantId);
      const addBtn = tile.querySelector('[data-byob-add]');
      const stepper = tile.querySelector('[data-byob-stepper]');
      const countEl = tile.querySelector('[data-byob-count]');

      if (item) {
        if (addBtn)  addBtn.hidden = true;
        if (stepper) stepper.hidden = false;
        if (countEl) countEl.textContent = item.qty;
        tile.classList.add('is-in-bundle');
      } else {
        if (addBtn)  addBtn.hidden = false;
        if (stepper) stepper.hidden = true;
        tile.classList.remove('is-in-bundle');
      }
    }

    // Build the count label string ("3 items" / "2 of 4 min" / "0 items")
    _countLabel(count) {
      const min = this.config.minItems || 0;
      // When a minimum is set, use the compact "X / N" format used in the sidebar pill.
      if (min > 0) return count + ' / ' + min;
      if (count === 0) return '0 items';
      return count + (count === 1 ? ' item' : ' items');
    }

    // Build the "Add $X more for Y% off!" hint string (or empty if no next tier)
    _nextTierHint(t) {
      const fmt = (c) => formatMoney(c, this.config.moneyFormat);
      if (t.nextTier) {
        const remaining = (t.nextTier.threshold * 100) - t.subtotal;
        return 'Add ' + fmt(remaining) + ' more for ' + t.nextTier.percent + '% off!';
      }
      if (t.activeIdx >= 0) return 'You unlocked the top tier — ' + t.activePct + '% off';
      const tiers = this.config.tiers || [];
      if (tiers.length) {
        const remaining = (tiers[0].threshold * 100) - t.subtotal;
        return 'Add ' + fmt(remaining) + ' more for ' + tiers[0].percent + '% off!';
      }
      return '';
    }

    // Update tier-chip classes (.is-unlocked / .is-current) on a NodeList
    _renderTierChips(chips, activeIdx) {
      if (!chips) return;
      chips.forEach((el) => {
        const idx = parseInt(el.getAttribute('data-byob-tier-chip'), 10);
        el.classList.toggle('is-unlocked', idx <= activeIdx);
        el.classList.toggle('is-current',  idx === activeIdx);
      });
    }

    // Render the bundle items list (with empty placeholders) into a given container.
    // Used by both the desktop sidebar and the mobile drawer — shared markup.
    _renderBundleItems(container) {
      if (!container) return;
      const fmt = (c) => formatMoney(c, this.config.moneyFormat);
      const minItems = this.config.minItems || 0;
      const minSlots = Math.max(minItems, 4); // always show at least 4 placeholder rows
      const html = [];

      const ICON_MINUS = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      const ICON_PLUS  = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      const ICON_X     = '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

      if (this.items.size === 0) {
        html.push('<div class="byob-sidebar__empty">' + this._esc(this.config.emptyText || '') + '</div>');
      } else {
        this.items.forEach((it) => {
          html.push(
            '<div class="byob-bundle-item">' +
              '<div class="byob-bundle-item__img">' +
                (it.image ? '<img src="' + this._esc(it.image) + '" alt="" loading="lazy">' : '') +
              '</div>' +
              '<div class="byob-bundle-item__body">' +
                '<a class="byob-bundle-item__title" href="' + this._esc(it.url) + '" target="_blank" rel="noopener">' + this._esc(it.title) + '</a>' +
                '<span class="byob-bundle-item__qty-controls">' +
                  '<button type="button" class="byob-bundle-item__qty-btn" data-byob-drawer-dec data-key="' + this._esc(it.key) + '" aria-label="Decrease quantity">' + ICON_MINUS + '</button>' +
                  '<span class="byob-bundle-item__qty-num">' + it.qty + '</span>' +
                  '<button type="button" class="byob-bundle-item__qty-btn" data-byob-drawer-inc data-key="' + this._esc(it.key) + '" aria-label="Increase quantity">' + ICON_PLUS + '</button>' +
                '</span>' +
              '</div>' +
              '<div class="byob-bundle-item__right">' +
                '<span class="byob-bundle-item__price">' + fmt(it.price * it.qty) + '</span>' +
                '<button type="button" class="byob-bundle-item__remove" data-byob-drawer-remove data-key="' + this._esc(it.key) + '" aria-label="Remove">' + ICON_X + '</button>' +
              '</div>' +
            '</div>'
          );
        });
      }

      // Append empty placeholder rows up to minSlots (always show at least minItems-many)
      const remaining = Math.max(0, minSlots - this.items.size);
      for (let k = 0; k < remaining; k++) {
        html.push('<div class="byob-bundle-placeholder">Select a product</div>');
      }

      container.innerHTML = html.join('');
    }

    _renderSidebar(t) {
      const n = this.nodes;
      if (!n.sidebar) return;
      const fmt = (c) => formatMoney(c, this.config.moneyFormat);

      if (n.sidebarCount)   n.sidebarCount.textContent = this._countLabel(t.count);
      this._renderTierChips(n.sidebarTierChips, t.activeIdx);
      this._renderBundleItems(n.sidebarItems);

      // Progress bar — fills toward the highest tier threshold (last marker at 100%).
      // Each marker dot lights up once the subtotal crosses its tier threshold.
      if (n.sidebarProgress) {
        const tiers = this.config.tiers || [];
        const top = tiers.length ? tiers[tiers.length - 1].threshold * 100 : 0;
        const pct = top > 0 ? Math.min(100, (t.subtotal / top) * 100) : 0;
        const prevPct = parseFloat(n.sidebarProgress.dataset.pct || '0');
        n.sidebarProgress.style.width = pct + '%';
        n.sidebarProgress.dataset.pct = String(pct);
        const track = n.sidebarProgress.parentElement;
        if (track) {
          const markers = track.querySelectorAll('.byob-sidebar__progress-marker');
          markers.forEach((m, i) => {
            const tier = tiers[i];
            const reached = tier && t.subtotal >= tier.threshold * 100;
            const wasActive = m.classList.contains('is-active');
            m.classList.toggle('is-active', !!reached);
            // Pulse a marker when it just unlocked
            if (reached && !wasActive) {
              m.classList.remove('byob-pulse');
              // force reflow so the animation restarts
              void m.offsetWidth;
              m.classList.add('byob-pulse');
            }
          });
        }
        // Pulse the fill bar whenever the value actually changes (product added/removed)
        if (Math.abs(pct - prevPct) > 0.5) {
          n.sidebarProgress.classList.remove('is-bumping');
          void n.sidebarProgress.offsetWidth;
          n.sidebarProgress.classList.add('is-bumping');
        }
      }

      // "What's left for next offer" — live message under the progress bar.
      // Always visible, even before the first item is added.
      if (n.sidebarNext && n.sidebarNextText) {
        const tiers = this.config.tiers || [];
        let label = '';
        let state = 'pending';
        if (t.nextTier) {
          const remaining = (t.nextTier.threshold * 100) - t.subtotal;
          label = 'Add ' + fmt(remaining) + ' to unlock ' + t.nextTier.percent + '% off';
        } else if (t.activeIdx >= 0) {
          label = "You've unlocked the top tier — " + t.activePct + '% off';
          state = 'unlocked';
        } else if (tiers.length) {
          const remaining = (tiers[0].threshold * 100) - t.subtotal;
          label = 'Add ' + fmt(remaining) + ' to unlock ' + tiers[0].percent + '% off';
        }
        n.sidebarNextText.textContent = label;
        n.sidebarNext.dataset.state = state;
        n.sidebarNext.hidden = !label;
      }

      if (n.sidebarSubtotal) n.sidebarSubtotal.textContent = fmt(t.subtotal);
      if (n.sidebarTotal)    n.sidebarTotal.textContent = fmt(t.total);
      if (n.sidebarSavingsRow) {
        if (t.discount > 0) {
          n.sidebarSavingsRow.hidden = false;
          if (n.sidebarSavings) n.sidebarSavings.textContent = '-' + fmt(t.discount);
        } else {
          n.sidebarSavingsRow.hidden = true;
        }
      }
      if (n.sidebarHint) {
        const hint = this._nextTierHint(t);
        n.sidebarHint.textContent = hint;
        n.sidebarHint.hidden = !hint || t.count === 0;
      }
    }

    _renderDrawer(t) {
      const n = this.nodes;
      if (!n.drawer) return;
      const fmt = (c) => formatMoney(c, this.config.moneyFormat);

      if (n.drawerCount)    n.drawerCount.textContent = this._countLabel(t.count);
      this._renderTierChips(n.drawerTierChips, t.activeIdx);
      this._renderBundleItems(n.drawerItems);

      if (n.drawerSubtotal) n.drawerSubtotal.textContent = fmt(t.subtotal);
      if (n.drawerTotal)    n.drawerTotal.textContent = fmt(t.total);
      if (n.drawerDiscRow) {
        if (t.discount > 0) {
          n.drawerDiscRow.hidden = false;
          if (n.drawerDiscount) n.drawerDiscount.textContent = '-' + fmt(t.discount);
        } else {
          n.drawerDiscRow.hidden = true;
        }
      }
      if (n.drawerHint) {
        const hint = this._nextTierHint(t);
        n.drawerHint.textContent = hint;
        n.drawerHint.hidden = !hint || t.count === 0;
      }
    }

    _renderTray(t) {
      const n = this.nodes;
      if (!n.tray) return;
      const fmt = (c) => formatMoney(c, this.config.moneyFormat);
      const tiers = this.config.tiers || [];

      // Count + savings text
      if (n.trayCount) n.trayCount.textContent = this._countLabel(t.count);
      if (n.traySavings) {
        if (t.activePct > 0) {
          n.traySavings.textContent = 'Save ' + t.activePct + '% — ' + fmt(t.discount);
          n.traySavings.hidden = false;
        } else {
          n.traySavings.hidden = true;
        }
      }

      // Total in the "View bundle" pill
      if (n.trayTotal) n.trayTotal.textContent = fmt(t.total);

      // Progress strip fill — proportional to top tier threshold
      if (n.barFill && tiers.length > 0) {
        const maxThreshold = tiers[tiers.length - 1].threshold * 100;
        const pct = Math.max(0, Math.min(100, (t.subtotal / maxThreshold) * 100));
        n.barFill.style.width = pct + '%';
      }
    }

    _renderCtas(t) {
      const meetsMin = (this.config.minItems || 0) === 0 || t.count >= this.config.minItems;
      const enabled = t.count > 0 && meetsMin;
      this.root.querySelectorAll('[data-byob-checkout]').forEach((btn) => {
        btn.disabled = !enabled;
      });
    }

    // ----- Checkout / Add to cart -------------------------------------------

    _submit(btn, action) {
      if (this.items.size === 0) return;
      btn.classList.add('is-loading');
      btn.disabled = true;
      this._hideError();

      const additions = [];
      this.items.forEach((it) => {
        additions.push({ id: parseInt(it.variantId, 10), quantity: it.qty });
      });

      const cartAddUrl = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) || '/cart/add.js';

      // Ask the cart drawer (and any other listeners) which sections they want
      // pre-rendered in the response. Using this pattern keeps the drawer's
      // markup in sync with the new cart in a single round-trip.
      const sectionsToBundle = [];
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
      } catch (e) { /* noop */ }

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: additions,
          sections: sectionsToBundle,
          section_url: window.location.pathname,
        }),
      })
        .then((res) => res.json().then((body) => ({ ok: res.ok, status: res.status, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error((body && body.description) || 'Could not add to cart');

          if (action === 'checkout') {
            window.location.href = '/checkout';
            return;
          }

          // action === 'cart' — open drawer, falling back to /cart only if
          // the drawer truly isn't available on this page.
          this._openCartOrRedirect(btn, body);
        })
        .catch((err) => {
          console.error('[BYOB] submit failed', err);
          this._showError(err.message || 'Something went wrong. Please try again.');
        })
        .finally(() => {
          // Always restore CTA states, regardless of success/failure. Without
          // this, an error mid-flight could leave the clicked button stuck
          // disabled even though the bundle still has items.
          btn.classList.remove('is-loading');
          this._refreshCtas();
        });
    }

    _openCartOrRedirect(triggerBtn, addResponse) {
      const cartDrawer = document.querySelector('cart-drawer');

      // No drawer mounted (extremely rare — overlay-group includes it on every
      // page) → fall back to the cart page.
      if (!cartDrawer || typeof cartDrawer.show !== 'function') {
        window.location.href = '/cart';
        return;
      }

      // Close any open BYOB-owned overlays first. The BYOB mobile bundle
      // drawer is at z-index: 200 and the featured-bundles modal is also
      // fixed-position; the cart drawer (z-index: 30) would otherwise open
      // behind them when "Add to cart" is clicked from inside.
      if (this.nodes.drawer && !this.nodes.drawer.hidden) this._closeDrawer();
      if (this.nodes.modal && !this.nodes.modal.hidden) this._closeModal();

      // IMPORTANT: /cart/add.js returns ONLY the newly-added line items — not
      // the full cart. The drawer's `onCartUpdate` handler reads
      // `event.cart.item_count` and `event.cart.sections[sectionId]`. If we
      // publish the add-response directly, item_count is undefined → the
      // drawer switches to its "empty cart" view (even though the real cart
      // is fine on the server).
      //
      // So we mirror bundle-widget.js's proven pattern:
      //   1. fetch /cart.js → get the FULL cart (item_count, totals, items, …)
      //   2. attach the `sections` we already have from the add response so
      //      the drawer doesn't need to re-fetch its own markup
      //   3. publish that merged object as the cartUpdate event
      //   4. open the drawer
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          if (addResponse && addResponse.sections) {
            cart.sections = addResponse.sections;
          }
          if (window.FoxTheme && window.FoxTheme.pubsub) {
            try {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart: cart }
              );
            } catch (e) { /* noop */ }
          }
          try { cartDrawer.show(triggerBtn); }
          catch (e) { window.location.href = '/cart'; return; }
          this._clearAfterAddToCart();
        })
        .catch(() => {
          // /cart.js failed — open the drawer anyway as a fallback. The
          // drawer's existing subscribers will eventually refresh it.
          try {
            cartDrawer.show(triggerBtn);
            this._clearAfterAddToCart();
          } catch (e) {
            window.location.href = '/cart';
          }
        });
    }

    _clearAfterAddToCart() {
      this.items.clear();
      this.render();
      // Drop any leftover loading state on every CTA — render()/_refreshCtas
      // already reset `disabled`, but `is-loading` is a presentation-only
      // class and must be cleared explicitly.
      this.root.querySelectorAll('[data-byob-checkout]').forEach((b) => {
        b.classList.remove('is-loading');
      });
    }

    // Idempotent CTA-state reset. Called from `_submit`'s finally so we can
    // recover from any mid-flight failure without leaving buttons stuck.
    _refreshCtas() {
      const t = this._totals();
      this._renderCtas(t);
    }

    _showError(msg) {
      if (!this.nodes.error) return;
      this.nodes.error.textContent = msg;
      this.nodes.error.hidden = false;
    }
    _hideError() {
      if (!this.nodes.error) return;
      this.nodes.error.hidden = true;
      this.nodes.error.textContent = '';
    }

    // ----- Utils -------------------------------------------------------------

    _esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    destroy() {
      document.removeEventListener('click', this._onClick, true);
      document.removeEventListener('change', this._onChange, true);
      document.removeEventListener('keydown', this._onKeydown);
    }
  }

  // ----- Boot -----------------------------------------------------------------

  function init() {
    document.querySelectorAll('[data-byob]').forEach((root) => {
      if (root.__byob) return;
      root.__byob = new Byob(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
